import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import './SellerProfile.css';
import { useNavigate } from 'react-router-dom';

const SellerProfile = ({ seller, onClick }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserToken();
    if (seller?.id) {
      fetchSellerProducts(seller.id);
    }
  }, [seller]);

  const loadUserToken = async () => {
    try {
      const token = localStorage.getItem('userToken');
      setUserToken(token);
    } catch (error) {
      console.error('Error loading user token:', error);
    }
  };

  const fetchSellerProducts = async (userId) => {
    try {
      setLoadingProducts(true);
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }
      
      const response = await fetch(`https://api.autocasses.com/api/produit/getproduits/${userId}`, {
        headers,
        timeout: 10000,
      });
      
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setSellerProducts(data.slice(0, 3)); // Limiter à 3 produits
        }
      }
    } catch (error) {
      console.error('Error fetching seller products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const getVerificationBadge = () => {
    if (seller?.isVerified) {
      return {
        icon: 'ShieldCheck',
        color: '#10b981',
        bg: 'rgba(16, 185, 129, 0.1)',
        label: 'Vendeur Vérifié'
      };
    }
    if (seller?.isPro) {
      return {
        icon: 'Building',
        color: '#8b5cf6',
        bg: 'rgba(139, 92, 246, 0.1)',
        label: 'Vendeur Professionnel'
      };
    }
    return {
      icon: 'User',
      color: '#6b7280',
      bg: 'rgba(156, 163, 175, 0.1)',
      label: 'Vendeur Particulier'
    };
  };

  const handleWhatsAppContact = (e) => {
    e.stopPropagation();
    if (!seller?.whatsappNumber) {
      alert('Numéro WhatsApp non disponible.');
      return;
    }

    const message = encodeURIComponent(
      `Bonjour ${seller.name}, je suis intéressé(e) par vos annonces sur AutoCasse.`
    );
    
    const whatsappUrl = `https://wa.me/${seller.whatsappNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCallSeller = (e) => {
    e.stopPropagation();
    if (!seller?.phoneNumber) {
      alert('Numéro de téléphone non disponible.');
      return;
    }
    window.location.href = `tel:${seller.phoneNumber}`;
  };

  const handleEmailContact = (e) => {
    e.stopPropagation();
    if (!seller?.email) {
      alert('Adresse email non disponible.');
      return;
    }
    window.location.href = `mailto:${seller.email}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long'
      }).format(date);
    } catch (error) {
      return 'Date inconnue';
    }
  };

  const getSellerTypeLabel = () => {
    if (seller?.type_pro === 'garage') return 'Garage';
    if (seller?.type_pro === 'mecanicien') return 'Mécanicien';
    if (seller?.isPro) return 'Professionnel';
    return 'Particulier';
  };

  const handleClick = (e) => {
        e.stopPropagation();
       navigate('/seller-profile', { 
      state: { 
        userId: seller.id,
        userName: seller.name 
      }
    });
    
  };

  const handleViewAllProducts = (e) => {
    e.stopPropagation();
    if (seller?.id) {
      window.location.href = `/rechercher?vendeur=${seller.id}`;
    }
  };

  const verification = getVerificationBadge();

  return (
    <div className="seller-profile-container" onClick={handleClick}>
      {/* Carte principale du vendeur */}
      <div className="seller-main-card">
        {/* En-tête avec avatar et informations principales */}
        <div className="seller-header">
          <div className="avatar-container">
            <img
              src={seller?.avatar}
              alt={seller?.name || 'Vendeur'}
              className="seller-avatar"
              onError={(e) => {
                e.target.src = '/assets/logo.png';
              }}
            />
            {seller?.isOnline && <div className="online-indicator" />}
          </div>
          
          <div className="seller-header-info">
            <div className="seller-name-row">
              <div className="seller-name" title={seller?.name || 'Vendeur'}>
                {seller?.name || 'Vendeur'}
              </div>
              <div className="verification-badge" style={{ backgroundColor: verification.bg }}>
                <Icon name={verification.icon} size={14} color={verification.color} />
                <span className="verification-text" style={{ color: verification.color }}>
                  {verification.label}
                </span>
              </div>
            </div>
            
            <div className="seller-type">
              {getSellerTypeLabel()}
            </div>
            
            <div className="location-row">
              <Icon name="MapPin" size={14} color="#64748b" />
              <span className="location-text" title={seller?.location?.city || seller?.adresse || 'Localisation non précisée'}>
                {seller?.location?.city || seller?.adresse || 'Localisation non précisée'}
              </span>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="seller-stats-container">
          <div className="stat-item">
            <div className="stat-value">{sellerProducts.length}</div>
            <div className="stat-label">Annonces</div>
          </div>
          
          <div className="stat-divider" />
          
          <div className="stat-item">
            <Icon name="Calendar" size={16} color="#3b82f6" />
            <div className="stat-label">
              Depuis {formatDate(seller?.joinDate)}
            </div>
          </div>
          
          <div className="stat-divider" />
          
          <div className="stat-item">
            <Icon name="Clock" size={16} color="#10b981" />
            <div className="stat-label">
              {seller?.responseTime || 'Rapid'}
            </div>
          </div>
        </div>

        {/* Description */}
        {seller?.descriptions && (
          <div className="description-section">
            <div className="section-title">À propos du vendeur</div>
            <div 
              className={`description-text ${showFullBio ? '' : 'description-truncated'}`}
              title={seller.descriptions}
            >
              {seller.descriptions}
            </div>
            {seller.descriptions.length > 150 && (
              <button 
                className="read-more-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullBio(!showFullBio);
                }}
              >
                {showFullBio ? 'Voir moins' : 'Lire la suite'}
              </button>
            )}
          </div>
        )}
       
        {/* Indicateurs de confiance */}
        <div className="trust-section">
          <div className="section-title">Confiance et sécurité</div>
          
          <div className="trust-indicators">
            {seller?.isVerified && (
              <div className="trust-item">
                <Icon name="ShieldCheck" size={16} color="#10b981" />
                <span className="trust-text">Identité vérifiée</span>
              </div>
            )}
            
            {seller?.phoneNumber && (
              <div className="trust-item">
                <Icon name="CheckCircle" size={16} color="#10b981" />
                <span className="trust-text">Téléphone vérifié</span>
              </div>
            )}
            
            {seller?.isPro && (
              <div className="trust-item">
                <Icon name="Building" size={16} color="#10b981" />
                <span className="trust-text">Professionnel</span>
              </div>
            )}
            
            <div className="trust-item">
              <Icon name="Shield" size={16} color="#3b82f6" />
              <span className="trust-text">Protection AutoCasse</span>
            </div>
          </div>
        </div>

     
      </div>

      {/* Boutons d'action */}
      <div className="seller-actions-container">
        <button 
          className="action-button whatsapp-button"
          onClick={handleWhatsAppContact}
        >
          <Icon name="MessageCircle" size={20} color="#fff" />
          <span className="action-button-text">WhatsApp</span>
        </button>
        
        <button 
          className="action-button call-button"
          onClick={handleCallSeller}
        >
          <Icon name="Phone" size={20} color="#fff" />
          <span className="action-button-text">Appeler</span>
        </button>
        
        {seller?.email && (
          <button 
            className="action-button email-button"
            onClick={handleEmailContact}
          >
            <Icon name="Mail" size={20} color="#fff" />
            <span className="action-button-text">Email</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SellerProfile;